import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { z } from 'zod';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkStation } from '@/lib/api/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

const stationSchema = z.object({
  name: z.string().min(2, 'stations.editDialog.nameRequired'),
});
type StationFormValues = z.infer<typeof stationSchema>;

interface Props {
  station: XkStation;
  onCancel: () => void;
  onSaved: (s: XkStation) => void;
  onDeleted?: () => void;
}

/**
 * Station editor, body only — hosted by the right rail (`FormPanelHost`),
 * not a modal. Replaces `EditStationDialog`.
 *
 * The journey picker the old dialog rendered is gone. It was already
 * `disabled` and existed only to point at Design Journey: the journey is set
 * on the event and cascades to every station, so a station-level control is
 * a dead end that suggests otherwise.
 */
export function StationForm({ station, onCancel, onSaved, onDeleted }: Props) {
  const queryClient = useQueryClient();
  const t = useT();
  // Two-step delete confirm — destructive and there is no undo.
  const [confirmDelete, setConfirmDelete] = useState(false);

  const form = useForm<StationFormValues>({
    resolver: zodResolver(stationSchema),
    defaultValues: { name: station.name },
  });

  useEffect(() => {
    form.reset({ name: station.name });
  }, [station, form]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.stations.detail(station.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(station.eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
  };

  const update = useMutation({
    mutationFn: (v: StationFormValues) => eventsApi.updateStation(station.id, { name: v.name }),
    onSuccess: (saved) => {
      invalidate();
      toast({ tone: 'success', title: t('stations.editDialog.updatedToast') });
      onSaved(saved as XkStation);
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stations.editDialog.updateFailed'),
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: () => eventsApi.deleteStation(station.id),
    onSuccess: () => {
      invalidate();
      toast({ tone: 'success', title: t('stations.editDialog.deletedToast') });
      onDeleted?.();
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stations.editDialog.deleteFailed'),
        description: (e as Error).message,
      }),
  });

  return (
    <form
      noValidate
      className="flex flex-1 min-h-0 flex-col"
      onSubmit={form.handleSubmit((v) => update.mutate(v))}
    >
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <FormField
          label={t('stations.editDialog.stationName')}
          required
          error={
            form.formState.errors.name?.message
              ? t(form.formState.errors.name.message)
              : undefined
          }
        >
          <Input {...form.register('name')} />
        </FormField>

        <p className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 text-[11px] text-[var(--color-fg-muted)]">
          {t('stations.form.journeyNote')}
        </p>

        {confirmDelete && (
          <div className="rounded-lg border border-[var(--color-error)] bg-[var(--color-error)]/10 p-3 text-xs text-[var(--color-fg-primary)]">
            <strong className="text-[var(--color-error)]">
              {t('stations.editDialog.confirmHeader')} "{station.name}"?
            </strong>{' '}
            {t('stations.editDialog.confirmBody')}
          </div>
        )}
      </div>

      {/*
        El pie ENVUELVE. Es la única defensa real contra que un botón se
        pierda: los botones llevan `whitespace-nowrap`, el carril está anclado
        a la derecha y sin `flex-wrap` el último —«Guardar cambios»— se salía
        por el borde de la pantalla al estrechar el panel. El tope de ancho
        mínimo ayuda, pero no basta: basta una traducción más larga.

        `mr-auto` en la acción destructiva sustituye al separador `flex-1` que
        había: hace lo mismo cuando los tres caben en una línea, y al envolver
        deja el borrado arriba a la izquierda con cancelar/guardar debajo, en
        vez de convertirse en un hueco elástico que empuja.
      */}
      <div className="flex flex-none flex-wrap items-center justify-end gap-2 border-t border-[var(--color-border-subtle)] p-3">
        <Button
          type="button"
          variant="ghost"
          className="mr-auto"
          onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}
          loading={remove.isPending}
          leftIcon={<Trash2 className="h-4 w-4" />}
        >
          {confirmDelete
            ? t('stations.editDialog.confirmDelete')
            : t('stations.editDialog.delete')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('stations.editDialog.cancel')}
        </Button>
        <Button type="submit" loading={update.isPending}>
          {t('stations.editDialog.save')}
        </Button>
      </div>
    </form>
  );
}
